import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Pencil, Layers, Upload, Download, Link2 } from 'lucide-react';
import { ModuleBanner } from '@/components/admin/ModuleBanner';
import { CompetencyImportDialog } from '@/components/admin/CompetencyImportDialog';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { useDataLabel } from '@/lib/dataI18n';

const CATEGORIES = ['behavioral', 'cognitive', 'analytical', 'leadership', 'operations', 'commercial', 'compliance', 'financial', 'human_resources', 'technical', 'technology', 'customer_service'];

const slug = (s) => s.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 60);

export default function AdminCompetencies() {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, object = edit
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', description: '', category: 'technical' });
  const [usageFor, setUsageFor] = useState(null); // competency being inspected
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const reload = () => {
    setLoading(true);
    adminApi.listCompetencies(showArchived)
      .then(setItems)
      .catch((e) => toast.error(t('toasts.failedToLoadCompetencies'), { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [showArchived]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((c) => {
      if (filterCat !== 'all' && c.category !== filterCat) return false;
      if (!s) return true;
      return (c.name || '').toLowerCase().includes(s) || (c.code || '').toLowerCase().includes(s);
    });
  }, [items, search, filterCat]);

  const sort = useSort();
  const sorted = useMemo(() => sort.apply(filtered, {
    name:     (c) => (c.name || '').toLowerCase(),
    code:     (c) => (c.code || '').toLowerCase(),
    category: (c) => (c.category || '').toLowerCase(),
    status:   (c) => (c.archived ? 1 : 0),
  }), [filtered, sort]);

  const openNew = () => { setEditing(null); setForm({ code: '', name: '', name_es: '', description: '', description_es: '', category: 'technical' }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ code: c.code, name: c.name || '', name_es: c.name_es || '', description: c.description || '', description_es: c.description_es || '', category: c.category || 'technical' }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await adminApi.updateCompetency(editing.id, {
          name: form.name, name_es: form.name_es, description: form.description,
          description_es: form.description_es, category: form.category,
        });
        toast.success(t('toasts.competencyUpdated'));
      } else {
        const code = form.code || slug(form.name);
        await adminApi.createCompetency({ ...form, code });
        toast.success(t('toasts.competencyCreated'));
      }
      setOpen(false); reload();
    } catch (e) { toast.error(t('toasts.saveFailed'), { description: formatApiError(e) }); }
  };

  const toggleArchive = async (c) => {
    try {
      await adminApi.updateCompetency(c.id, { archived: !c.archived });
      toast.success(c.archived ? 'Reactivated' : 'Archived');
      reload();
    } catch (e) { toast.error(t('toasts.failed'), { description: formatApiError(e) }); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await adminApi.exportCompetencies();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CTH_competency_library_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(t('toasts.failed'), { description: formatApiError(e) }); }
    finally { setExporting(false); }
  };

  const openUsage = async (c) => {
    setUsageFor(c); setUsage(null); setUsageLoading(true);
    try {
      const data = await adminApi.competencyUsage(c.id);
      setUsage(data.usage);
    } catch (e) { toast.error(t('toasts.failed'), { description: formatApiError(e) }); setUsageFor(null); }
    finally { setUsageLoading(false); }
  };

  return (
    <>
      <Topbar title={t('pages.competencies.pageTitle')} subtitle={t('pages.competencies.pageSubtitle')} />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-competencies-page">
        <ModuleBanner id="competencies" icon={Layers} />
        <Card className="p-4 shadow-card">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="comp-search" placeholder={t('pages.competencies.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-full md:w-48 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pages.competencies.allCategories')}</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`pages.competencies.cat.${c}`, { defaultValue: c.replace('_', ' ') })}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 px-3 h-10 rounded-md border border-border">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="comp-archived-toggle" />
              <Label className="text-xs">{t('pages.competencies.showArchived')}</Label>
            </div>
            <Button data-testid="comp-export-btn" variant="outline" onClick={exportExcel} disabled={exporting} className="h-10 gap-1.5">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t('pages.competencies.exportButton', { defaultValue: 'Export Excel' })}
            </Button>
            <Button data-testid="comp-import-btn" variant="outline" onClick={() => setImportOpen(true)} className="h-10 gap-1.5">
              <Upload className="h-4 w-4" /> {t('pages.competencies.import.button', { defaultValue: 'Import competencies' })}
            </Button>
            <Button data-testid="comp-new-btn" onClick={openNew} className="bg-gradient-primary h-10 gap-1.5"><Plus className="h-4 w-4" /> {t('pages.competencies.newCompetency')}</Button>
          </div>
        </Card>

        <CompetencyImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={reload} />

        <Card className="shadow-card overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.competencies.loading')}</div>
          ) : sorted.length === 0 ? (
            <div className="p-12 text-center"><Layers className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" /><p className="text-sm text-muted-foreground">{t('pages.competencies.noMatch')}</p></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow><TableHead><SortableHeader sort={sort} k="name">{t('pages.competencies.name')}</SortableHeader></TableHead><TableHead><SortableHeader sort={sort} k="code">{t('pages.competencies.code')}</SortableHeader></TableHead><TableHead><SortableHeader sort={sort} k="category">{t('pages.competencies.category')}</SortableHeader></TableHead><TableHead><SortableHeader sort={sort} k="status">{t('pages.competencies.status')}</SortableHeader></TableHead><TableHead className="text-right">{t('pages.competencies.actions')}</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((c) => (
                  <TableRow key={c.id} data-testid={`comp-row-${c.code}`}>
                    <TableCell>
                      <p className="font-medium">{tData({ name: c.name, name_es: c.name_es })}</p>
                      <p className="text-xs text-muted-foreground">{tData({ name: c.description, name_es: c.description_es })}</p>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.code}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{t(`pages.competencies.cat.${c.category}`, { defaultValue: tData((c.category || '—').replace('_', ' ')) })}</Badge></TableCell>
                    <TableCell>{c.archived ? <Badge variant="outline" className="text-muted-foreground">{t('common.statusValues.archived', { defaultValue: 'Archived' })}</Badge> : <Badge className="bg-success/10 text-success border-success/20" variant="outline">{t('common.statusValues.active', { defaultValue: 'Active' })}</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" data-testid={`comp-usage-${c.code}`} onClick={() => openUsage(c)} className="gap-1 text-muted-foreground"><Link2 className="h-3.5 w-3.5" /> {t('pages.competencies.usage.button', { defaultValue: 'Usage' })}</Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="gap-1"><Pencil className="h-3.5 w-3.5" /> {t('pages.competencies.edit')}</Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleArchive(c)} className="gap-1 text-muted-foreground">{c.archived ? t('pages.competencies.reactivate') : t('pages.competencies.archive')}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t('pages.competencies.editTitle') : t('pages.competencies.newTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>{t('pages.competencies.nameEn', { defaultValue: 'Name (EN)' })}</Label><Input data-testid="comp-form-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>{t('pages.competencies.nameEs', { defaultValue: 'Nombre (ES)' })}</Label><Input data-testid="comp-form-name-es" className="mt-1.5" value={form.name_es} onChange={(e) => setForm({ ...form, name_es: e.target.value })} /></div>
            </div>
            {!editing && <div><Label>{t('pages.competencies.code')}</Label><Input data-testid="comp-form-code" className="mt-1.5" placeholder={slug(form.name)} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /><p className="text-xs text-muted-foreground mt-1">{t('pages.competencies.codeHint')}</p></div>}
            <div><Label>{t('pages.competencies.descEn', { defaultValue: 'Description (EN)' })}</Label><Textarea data-testid="comp-form-desc" className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>{t('pages.competencies.descEs', { defaultValue: 'Descripción (ES)' })}</Label><Textarea data-testid="comp-form-desc-es" className="mt-1.5" value={form.description_es} onChange={(e) => setForm({ ...form, description_es: e.target.value })} /></div>
            <div><Label>{t('pages.competencies.category')}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`pages.competencies.cat.${c}`, { defaultValue: c.replace('_', ' ') })}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('pages.competencies.cancel')}</Button><Button data-testid="comp-form-save" onClick={save} className="bg-gradient-primary">{editing ? t('pages.competencies.save') : t('pages.competencies.create')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!usageFor} onOpenChange={(v) => { if (!v) setUsageFor(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-[700px] max-h-[80vh] overflow-y-auto" data-testid="comp-usage-dialog">
          <DialogHeader><DialogTitle className="break-words pr-8">{t('pages.competencies.usage.title', { defaultValue: 'Competency usage' })} — {usageFor ? tData(usageFor.name) : ''}</DialogTitle></DialogHeader>
          {usageLoading ? (
            <div className="py-8 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}</div>
          ) : usage && (
            <div className="space-y-3 text-sm">
              <div className={`rounded-md border p-3 ${usage.in_use ? 'border-warning/40 bg-warning/5' : 'border-success/40 bg-success/5'}`} data-testid="comp-usage-verdict">
                {usage.in_use
                  ? t('pages.competencies.usage.inUse', { defaultValue: 'This competency is in use. It is protected: it can be archived, but never permanently deleted.' })
                  : t('pages.competencies.usage.notUsed', { defaultValue: 'This competency has never been used. It can be archived, or permanently deleted from the Trash workspace.' })}
              </div>
              <div className="space-y-2">
                <UsageRow label={t('pages.competencies.usage.questionnaires', { defaultValue: 'Questionnaires' })} count={usage.questionnaires.length}
                  names={usage.questionnaires.map((q) => q.name)} tData={tData} />
                <UsageRow label={t('pages.competencies.usage.roleProfiles', { defaultValue: 'Role Profiles' })} count={usage.role_profiles.length}
                  names={usage.role_profiles.map((p) => p.position_title)} tData={tData} />
                <UsageRow label={t('pages.competencies.usage.positionRequirements', { defaultValue: 'Position requirements (TalentCompetency)' })} count={usage.position_requirements} tData={tData} />
                <UsageRow label={t('pages.competencies.usage.competencyProfiles', { defaultValue: 'Competency profiles' })} count={usage.competency_profiles.length}
                  names={usage.competency_profiles.map((p) => p.name)} tData={tData} />
                <UsageRow label={t('pages.competencies.usage.reports', { defaultValue: 'Historical reports with results' })} count={usage.reports_with_results} tData={tData} />
              </div>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setUsageFor(null)}>{t('pages.competencies.cancel')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const UsageRow = ({ label, count, names, tData }) => (
  <div className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2">
    <div className="min-w-0">
      <p className="font-medium">{label}</p>
      {count > 0 && names?.length > 0 && (
        <p className="text-xs text-muted-foreground break-words">{names.slice(0, 5).map((n) => tData(n)).join(', ')}{names.length > 5 ? ` +${names.length - 5}` : ''}</p>
      )}
    </div>
    <Badge variant={count ? 'default' : 'outline'} className={count ? 'bg-primary/10 text-primary border-primary/20' : 'text-muted-foreground'}>{count}</Badge>
  </div>
);
